import { hasMatchingTimes, formatStringForComparison } from '../utils/architecture.utils';

const entityPropertyByType = {
  movies: 'title',
  placeholder: 'blockName',
  series: 'seriesName',
  titlegroup: 'titleGroupName',
};

const isRepeat = (block) => formatStringForComparison(block.type) === 'repeat';

const hasMatchingEntity = (firstBlock, secondBlock) => {
  const firstType = formatStringForComparison(firstBlock.type);
  const secondType = formatStringForComparison(secondBlock.type);

  // If both blocks are repeats, they are considered related regardless of their entity properties.
  if (isRepeat(firstBlock) && isRepeat(secondBlock)) return true;

  // If the two blocks are different types and neither is a repeat, they are considered related.
  if (firstType !== secondType && !isRepeat(firstBlock) && !isRepeat(secondBlock)) return true;

  // If the two blocks are the same type, check if they have matching entity properties
  // (e.g., SeriesName, TitleGroupName, etc.)
  const entityType = isRepeat(firstBlock) ? secondType : firstType;
  const entityProperty = entityPropertyByType[entityType];
  const firstEntity = firstBlock[entityProperty];
  const secondEntity = secondBlock[entityProperty];

  // If either entity is missing, we consider them related. Otherwise, we compare their values.
  return (
    !firstEntity ||
    !secondEntity ||
    formatStringForComparison(firstEntity) === formatStringForComparison(secondEntity)
  );
};

const getPreferredCandidates = (candidates, currentBlock) => {
  const matchingCandidates = candidates.filter((candidate) => {
    return hasMatchingEntity(currentBlock, candidate);
  });

  return matchingCandidates.length ? matchingCandidates : candidates;
};

/**
 * Performs a bi-directional graph traversal to find all interconnected blocks.
 *
 * It builds an adjacency map where blocks are nodes and `blockReference`/`blockName`
 * relationships are edges. It then uses Breadth-First Search (BFS) to find every
 * block reachable from the `startBlock`, whether it is a parent, child, or sibling.
 * Explicit links between non-repeat blocks may cross program types and entity names.
 *
 * @param {object} startBlock - The normalized block object to start the search from.
 * @param {object[]} allEvents - The pool of events to search within.
 * @returns {object[]} An array containing every block in the interconnected chain.
 */
const findEntireChain = (startBlock, allEvents, includeRepeats) => {
  // 1. PRE-PROCESSING: Build an Adjacency Map (Index)
  const adjacencyMap = new Map();
  const eventsToTraverse = includeRepeats
    ? allEvents
    : allEvents.filter((event) => {
        const isStartBlock = event.id === startBlock.id;
        const isRepeatEvent = formatStringForComparison(event.type) === 'repeat';
        return isStartBlock || !isRepeatEvent;
      });

  // Helper to add connections bi-directionally
  const addConnection = (key, block) => {
    if (!key) return;
    if (!adjacencyMap.has(key)) adjacencyMap.set(key, []);
    adjacencyMap.get(key).push(block);
  };

  eventsToTraverse.forEach((event) => {
    // Index by the block's own name (so others can find it via reference)
    addConnection(event.blockName, event);
    // Index by the block's reference (so it can find its target)
    addConnection(event.blockReference, event);
  });

  // 2. TRAVERSAL: Use the map
  const queue = [startBlock];
  const visited = new Set([startBlock.id]);
  const entireChain = [];

  while (queue.length > 0) {
    const currentBlock = queue.shift();
    entireChain.push(currentBlock);

    // Collect blocks indexed under the current block's name or reference.
    // This is a broad candidate set; entity matching and the explicit link check
    // below determine which candidates are actual neighbors.
    const potentialNeighbors = getPreferredCandidates(
      [
        ...(adjacencyMap.get(currentBlock.blockName) || []),
        ...(adjacencyMap.get(currentBlock.blockReference) || []),
      ]
        .filter((block) => block.id !== currentBlock.id)
        .filter(
          (block, index, blocks) => blocks.findIndex((item) => item.id === block.id) === index,
        ),
      currentBlock,
    );

    potentialNeighbors.forEach((otherBlock) => {
      if (!visited.has(otherBlock.id)) {
        const isConnected =
          currentBlock.blockReference === otherBlock.blockName ||
          otherBlock.blockReference === currentBlock.blockName;

        if (isConnected) {
          visited.add(otherBlock.id);
          queue.push(otherBlock);
        }
      }
    });
  }

  return entireChain;
};

/**
 * Finds a list of blocks related to a selected block based on configurable business rules.
 *
 * The retrieval logic consists of two main phases:
 * 1. **Core Chain Search**: Finds an interconnected chain of non-repeat blocks.
 *    - Explicit links may connect different program types and entity names.
 *    - By default (`searchEntireSchedule: true`), this searches the full graph across the week.
 *    - If `searchEntireSchedule` is false, it restricts the initial search to the same Time Slot.
 * 2. **Repeat Search**: Optionally finds REPEAT blocks that reference blocks in the core chain.
 *
 * @param {object} selected - The block object that the user selected. Expects
 * properties like `id`, `type`, `startTime`, `endTime`, `seriesName`, etc. (same shape as
 * the entries in `allEvents`).
 * @param {object[]} allEvents - An array of all normalized block objects currently present on the schedule.
 * @param {object} [options] - Configuration options to control the search scope.
 * @param {boolean} [options.searchEntireSchedule=true] - Defaults to `true`.
 * - If `true`, performs a full graph traversal to find all linked blocks regardless of their time or day.
 * - If `false`, restricts the search to blocks occurring in the exact same Time Slot (useful for specific slot updates).
 * @param {boolean} [options.requireExactName=true] - Defaults to `true` for the restricted same-time search.
 * - Controls visible-name matching during the restricted search. Explicit full-schedule links
 *   may still connect blocks with different names or program types.
 * @param {boolean} [options.includeRepeats=true] - Defaults to `true`. If `true`, searches the entire schedule for REPEAT blocks linked to the core results.
 * @param {boolean} [options.includeSelected=false] - Defaults to `false`. If `true`, the returned array includes the originally `selected` block. If `false`, it is filtered out.
 * @returns {object[]} A sorted array (Mon->Sun) of related block objects. Returns an empty array if no related blocks are found.
 */
const findRelatedBlocks = (
  selected,
  allEvents,
  {
    includeRepeats = true,
    includeSelected = false,
    requireExactName = true,
    searchEntireSchedule = true,
  } = {},
) => {
  const types = {
    REPEAT: 'repeat',
    SERIES: 'series',
    TITLEGROUP: 'titlegroup',
    MOVIES: 'movies',
    PLACEHOLDER: 'placeholder',
  };

  // 1. Configuration Map: Define which property to match for each type
  const typeConfig = {
    [types.SERIES]: { normProp: 'seriesName' },
    [types.TITLEGROUP]: { normProp: 'titleGroupName' },
    [types.MOVIES]: { normProp: 'title' },
    [types.PLACEHOLDER]: { normProp: 'blockName' },
  };

  let relatedBlocks = [];

  const selectedType = formatStringForComparison(selected?.type ?? '');
  const fullSelectedBlock = allEvents.find((block) => block.id === selected.id);
  if (!fullSelectedBlock) return relatedBlocks;

  // 2. Get the config for the current type
  const config = typeConfig[selectedType];
  let eventsToSearch = allEvents;

  // PRE-FILTER: Scope the initial search to a specific Time Slot and Entity Type.
  //
  // 1. Time Scope: When updating a "Daily News" Series at 8:00 AM, we only want to affect
  //    other "Daily News" blocks at 8:00 AM. We must exclude the "Daily News" blocks at
  //    6:00 PM, even though they share the same name/reference.
  //
  // 2. Type Scope: The restricted search matches the selected Block Type. Full-schedule
  //    traversal follows explicit links across program types.
  //
  // 3. Name Scope: Controlled by `requireExactName` for the restricted search. This prevents
  //    unrelated same-slot records from being selected while preserving explicit full-schedule links.
  if (!searchEntireSchedule && selectedType !== types.REPEAT && config) {
    eventsToSearch = allEvents.filter((block) => {
      // A. Check if the names match (seriesName vs seriesName, etc.)
      const namesMatch = (block[config.normProp] ?? null) === (selected[config.normProp] ?? null);

      // B. Determine if we should enforce that match
      // If requireExactName is TRUE, we strictly require namesMatch to be true.
      // If requireExactName is FALSE, we ignore the name mismatch (allow loose graph linking).
      const satisfiesNameRequirement = requireExactName ? namesMatch : true;

      return (
        formatStringForComparison(block.type) === selectedType &&
        satisfiesNameRequirement &&
        hasMatchingTimes(block, {
          startTime: selected.startTime,
          endTime: selected.endTime,
        })
      );
    });
  }

  // Step 3: Find the entire interconnected chain starting from the selected block
  // using the scope defined above.
  const coreBlocks = findEntireChain(
    fullSelectedBlock,
    eventsToSearch,
    includeRepeats && selectedType === types.REPEAT,
  );

  // Step 4: Find REPEAT blocks that reference a block in the chain and match its entity metadata.
  // A non-repeat chain never traverses Repeat records directly.
  let repeatMatches = [];
  if (includeRepeats) {
    const blockNamesToFind = new Set(coreBlocks.map((block) => block.blockName));
    repeatMatches = allEvents.filter(
      (block) =>
        formatStringForComparison(block.type) === types.REPEAT &&
        blockNamesToFind.has(block.blockReference) &&
        coreBlocks.some(
          (coreBlock) =>
            coreBlock.blockName === block.blockReference && hasMatchingEntity(block, coreBlock),
        ),
    );
  }

  // 5. Merge & Deduplicate
  //
  // Repeat records are collected explicitly so non-repeat traversal can exclude them while
  // still allowing matching Repeat dependents to be returned.
  //
  // Since a block might be found by BOTH methods, we use a Map by ID to squash duplicates
  // and ensure every block appears only once in the final list.
  const uniqueMap = new Map();
  [...coreBlocks, ...repeatMatches].forEach((b) => uniqueMap.set(b.id, b));
  relatedBlocks = Array.from(uniqueMap.values());

  // 6. Sort: Mon(1) -> Sun(7), then by Start Time
  relatedBlocks.sort((a, b) => {
    const dayDiff = (a.dayOfWeek || 0) - (b.dayOfWeek || 0);
    if (dayDiff !== 0) return dayDiff;
    return (a.startTime || '').localeCompare(b.startTime || '');
  });

  // return everything including target block
  if (includeSelected) {
    return relatedBlocks;
  }

  // Ensure the originally selected block is not in the list.
  return relatedBlocks.filter((block) => block.id !== selected.id);
};

export default findRelatedBlocks;
